/**
 * @file This is the socket io service module
 * @copyright Andreas Horvath 2016-2024
 * @version 0.2.0
 */
import { Injectable, NgZone } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CsrfService } from '../csrf/csrf.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { RequestFailure } from '../maintenance/failure-diagnostics';

declare var io: any;

@Injectable({
  providedIn: 'root',
})
export class SocketIoService {
  private URL = environment.SERVER_URL;
  private API_PREFIX = '/';

  private socket: any;
  private pendingInitialization?: Promise<void>;

  constructor(private ngZone: NgZone, private csrfService: CsrfService, private http: HttpClient) {
    io.sails.url = this.URL;
    io.sails.reconnection = true;
    this.socket = io.sails.connect();
  }

  /**
   * checks if io.socket is ocnnected
   * @returns {Boolean}
   */
  get connected(): boolean {
    return this.socket.isConnected();
  }

  // Shared by the page and connect listener: never start two initializations at once.
  public initializePlayer(): Promise<void> {
    if (!this.pendingInitialization) {
      this.pendingInitialization = this.initializePlayerOnce()
        .finally(() => { this.pendingInitialization = undefined; });
    }
    return this.pendingInitialization;
  }

  private async initializePlayerOnce(): Promise<void> {
    await this.waitForConnection();
    await this.csrfService.refreshTokenStrict();
    let refreshedSession = false;
    let refreshedCsrf = false;
    let retriedServer = false;
    for (;;) {
      try {
        await this.postInitialization();
        return;
      } catch (error) {
        if (!(error instanceof RequestFailure)) throw error;
        if (error.kind === 'CSRF' && !refreshedCsrf) {
          refreshedCsrf = true;
          await this.csrfService.refreshTokenStrict();
          continue;
        }
        if (error.status === 401 && !refreshedSession) {
          refreshedSession = true;
          // Use an uncached HTTP check. A network failure must NOT count as logged out.
          const session = await firstValueFrom(this.http.get<{ isLoggedIn: boolean }>(
            `${environment.API_SERVER_URL}/user/auth/is-logged-in`,
            { withCredentials: true, params: { _: Date.now() } }
          ).pipe(timeout(10000)));
          if (session?.isLoggedIn === false) throw new RequestFailure('SESSION', 401);
          if (session?.isLoggedIn !== true) throw new RequestFailure('REJECTED', 401);
          if (this.connected) this.socket.disconnect();
          if (!this.socket.isConnecting()) this.socket.reconnect();
          await this.waitForConnection();
          await this.csrfService.refreshTokenStrict();
          continue;
        }
        // Only retry an explicit temporary failure of user/init. Never replay game actions
        // or a request whose result is unknown because its acknowledgement timed out.
        if ([502, 503, 504].includes(error.status!) && !retriedServer) {
          retriedServer = true;
          await new Promise((resolve) => setTimeout(resolve, 750));
          await this.waitForConnection();
          continue;
        }
        throw error;
      }
    }
  }

  private waitForConnection(): Promise<void> {
    // A socket handshake can finish before the browser's HTTP networking comes
    // back online. Don't immediately fail the following CSRF/session HTTP reads.
    const ready = () => this.connected && navigator.onLine !== false;
    if (ready()) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const connected = () => { if (ready()) { cleanup(); resolve(); } };
      const timer = setTimeout(() => { cleanup(); reject(new RequestFailure('CONNECTION')); }, 10000);
      const cleanup = () => {
        clearTimeout(timer);
        this.socket.off('connect', connected);
        window.removeEventListener('online', connected);
      };
      this.socket.on('connect', connected);
      window.addEventListener('online', connected);
      connected();
    });
  }

  private postInitialization(): Promise<void> {
    if (!this.connected) return Promise.reject(new RequestFailure('CONNECTION'));
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new RequestFailure('TIMEOUT')), 15000);
      try {
        this.socket.post('/user/init', { _csrf: (window as any).csrfToken }, (body, response) => {
          clearTimeout(timer);
          const status = response?.statusCode;
          if (status >= 200 && status < 300) resolve();
          else {
            const csrf = status === 403 &&
              /csrf/i.test(typeof body === 'string' ? body : body?.message || body?.code || '');
            reject(new RequestFailure(csrf ? 'CSRF' : 'REJECTED', status));
          }
        });
      } catch (error) {
        clearTimeout(timer);
        reject(error);
      }
    });
  }

  /**
   * `withStatus`: a rejected request fails with a RequestFailure('REJECTED', status) that
   * carries the server's answer as `body`, instead of the bare body. Callers that must
   * tell "gone" (4xx) from "try again" (5xx) need the status; the default keeps the bare
   * body that existing callers compare against.
   */
  private request(method: 'get' | 'post', url: string, data?: any, withStatus = false): Promise<any> {
    // sails.io otherwise buffers offline actions and sends them after reconnect,
    // when the turn or even the game may already have changed.
    if (!this.connected || navigator.onLine === false) return Promise.reject(Object.assign(new RequestFailure('CONNECTION'), { requestSent: false }));
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (error: boolean, value: any) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.socket.off('disconnect', disconnected);
        error ? reject(value) : resolve(value);
      };
      const disconnected = () => finish(true, new RequestFailure('CONNECTION'));
      const timer = setTimeout(() => finish(true, new RequestFailure('TIMEOUT')), 15000);
      this.socket.on('disconnect', disconnected);
      try {
        this.socket[method]('/' + url.replace(/^\/+/, ''), data, (body, response) => {
          const status = response?.statusCode;
          if (status >= 200 && status < 300) finish(false, body);
          else if (status === 403 && body === 'CSRF mismatch') finish(true, new RequestFailure('CSRF', status));
          else if (withStatus) finish(true, Object.assign(new RequestFailure('REJECTED', status), { body }));
          else finish(true, body ?? new RequestFailure('REJECTED', status));
        });
      } catch (error) { finish(true, error); }
    });
  }

  public get(url: string, data?: any): Promise<any> {
    return this.request('get', url, data);
  }

  public async post(url: string, data?: any, options: { withStatus?: boolean } = {}): Promise<any> {
    const send = () => this.request('post', url, { ...data, _csrf: (window as any).csrfToken }, !!options.withStatus);
    try { return await send(); }
    catch (error) {
      // Only an explicit CSRF rejection proves the action did not run.
      if (!(error instanceof RequestFailure) || error.kind !== 'CSRF') throw error;
      await this.csrfService.refreshTokenStrict();
      return send(); // One retry only; disconnect and timeout never replay an action.
    }
  }

  public on(eventName: string, fun: Function): void {
    const zone = this.ngZone;
    this.socket.on(eventName, function (data) {
      zone.run(() => {
        fun(data);
      });
    });
  }

  public onMultiple(eventNames: string[], fun: Function): void {
    const zone = this.ngZone;
    for (const eventName of eventNames) {
      this.socket.on(eventName, function (data) {
        zone.run(() => {
          fun(data);
        });
      });
    }
  }
}
